export interface CarbonProject {
  id: string;
  name: string;
  type: string;
  client: string;
  municipality: string;
  uf: string;
  areaHa: number;
  treesCount: number;
  biomassEstimated: number;
  carbonEstimated: number;
  co2eEstimated: number;
  confidenceLevel: number;
  status: string;
  lastUpdate: string;
  methodology: string;
  description: string;
  createdAt: string;
}

export interface TreeCarbonEstimate {
  id: string;
  ctaCode: string;
  species: string;
  speciesCommon: string;
  projectId: string;
  municipality: string;
  neighborhood: string;
  dap: number;
  height: number;
  biomassEstimated: number;
  carbonEstimated: number;
  co2eEstimated: number;
  methodology: string;
  confidenceLevel: "alta" | "média" | "baixa";
  lastInspection: string;
  greenAreaId: string | null;
  hasDap: boolean;
  hasHeight: boolean;
  hasSpecies: boolean;
  hasCoords: boolean;
  hasInspection: boolean;
}

export interface CarbonArea {
  id: string;
  name: string;
  type: string;
  areaHa: number;
  treesCount: number;
  co2eEstimated: number;
  criticalRiskCount: number;
  confidenceLevel: number;
  status: string;
  municipality: string;
  projectId: string;
}

export interface CarbonCompensation {
  id: string;
  projectId: string;
  projectName: string;
  areaName: string;
  obligationType: string;
  seedlingsPlanned: number;
  seedlingsPlanted: number;
  survivalRate: number;
  inspectionsDone: number;
  co2ePreliminary: number;
  status: string;
  lastInspection: string;
}

export interface CarbonMethodology {
  id: string;
  name: string;
  description: string;
  requiredData: string[];
  precisionLevel: string;
  limitations: string;
  status: "ativa" | "inativa";
  type: string;
}

export interface CarbonReport {
  id: string;
  title: string;
  description: string;
  lastGenerated: string | null;
  status: "disponível" | "gerando" | "pendente";
  format: string;
  type: string;
}

export interface CarbonMonthlyData {
  month: string;
  co2e: number;
  carbon: number;
  biomass: number;
}

export interface CarbonByProjectData {
  project: string;
  co2e: number;
  carbon: number;
  biomass: number;
}

export interface CarbonByAreaTypeData {
  type: string;
  co2e: number;
  label: string;
}

export interface CarbonQualityMetric {
  label: string;
  value: number;
  target: number;
  unit: string;
  status: "bom" | "regular" | "ruim";
}

export interface CarbonAlert {
  id: string;
  type: string;
  message: string;
  severity: "crítico" | "atenção" | "cautela";
  count: number;
  action: string;
}

export interface CarbonNeighborhoodRank {
  name: string;
  co2e: number;
  treesCount: number;
  avgConfidence: number;
}

export const carbonProjects: CarbonProject[] = [
  {
    id: "cp-001",
    name: "Inventário Arbóreo Goiânia Norte",
    type: "Carbono Urbano",
    client: "Prefeitura de Goiânia",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 45.2,
    treesCount: 3240,
    biomassEstimated: 862.4,
    carbonEstimated: 345.0,
    co2eEstimated: 1265.0,
    confidenceLevel: 78,
    status: "Em monitoramento",
    lastUpdate: "2026-06-28",
    methodology: "DAP + Altura por Espécie",
    description: "Inventário arbóreo completo da região norte de Goiânia com estimativa preliminar de biomassa e carbono.",
    createdAt: "2025-03-15",
  },
  {
    id: "cp-002",
    name: "Arborização Loteamento Jardim Verde",
    type: "Arborização Urbana",
    client: "Incorporadora VerdeVida",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 12.8,
    treesCount: 890,
    biomassEstimated: 245.3,
    carbonEstimated: 98.1,
    co2eEstimated: 359.5,
    confidenceLevel: 82,
    status: "Em estimativa",
    lastUpdate: "2026-06-25",
    methodology: "DAP por Grupo Funcional",
    description: "Projeto de arborização de loteamento com compensação ambiental integrada.",
    createdAt: "2025-07-10",
  },
  {
    id: "cp-003",
    name: "Compensação Reserva Ecológica Buritis",
    type: "Compensação Ambiental",
    client: "Construtora Horizonte",
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    areaHa: 28.5,
    treesCount: 1560,
    biomassEstimated: 520.7,
    carbonEstimated: 208.3,
    co2eEstimated: 763.6,
    confidenceLevel: 65,
    status: "Em levantamento",
    lastUpdate: "2026-06-20",
    methodology: "Estimativa por Área Verde",
    description: "Área de compensação ambiental com replantio nativo e monitoramento de sobrevivência.",
    createdAt: "2025-09-01",
  },
  {
    id: "cp-004",
    name: "Reflorestamento APP Córrego Matinha",
    type: "Replantio",
    client: "Secretaria de Meio Ambiente",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 8.4,
    treesCount: 620,
    biomassEstimated: 186.0,
    carbonEstimated: 74.4,
    co2eEstimated: 272.7,
    confidenceLevel: 70,
    status: "Em monitoramento",
    lastUpdate: "2026-06-27",
    methodology: "Estimativa Preliminar Compensação",
    description: "Replantio em APP urbana com espécies nativas e acompanhamento de crescimento.",
    createdAt: "2025-11-20",
  },
  {
    id: "cp-005",
    name: "Recuperação Área Degradada Setor Industrial",
    type: "Recuperação de Área Degradada",
    client: "Indústria Metalúrgica Central",
    municipality: "Anápolis",
    uf: "GO",
    areaHa: 15.6,
    treesCount: 1100,
    biomassEstimated: 330.5,
    carbonEstimated: 132.2,
    co2eEstimated: 484.6,
    confidenceLevel: 60,
    status: "Planejado",
    lastUpdate: "2026-06-15",
    methodology: "DAP + Altura por Espécie",
    description: "Projeto de recuperação de área degradada com plantio de espécies pioneiras e secundárias.",
    createdAt: "2026-01-10",
  },
  {
    id: "cp-006",
    name: "Parque Municipal Buriti Sereno",
    type: "UC / Área Protegida",
    client: "Prefeitura de Goiânia",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 32.0,
    treesCount: 2100,
    biomassEstimated: 680.0,
    carbonEstimated: 272.0,
    co2eEstimated: 997.3,
    confidenceLevel: 85,
    status: "Em monitoramento",
    lastUpdate: "2026-06-29",
    methodology: "DAP + Altura por Espécie",
    description: "Inventário e estimativa de carbono do patrimônio arbóreo do parque municipal.",
    createdAt: "2024-08-05",
  },
  {
    id: "cp-007",
    name: "Corredor Verde Avenida T-63",
    type: "Corredor Verde",
    client: "Prefeitura de Goiânia",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 6.2,
    treesCount: 450,
    biomassEstimated: 135.0,
    carbonEstimated: 54.0,
    co2eEstimated: 198.0,
    confidenceLevel: 75,
    status: "Em estimativa",
    lastUpdate: "2026-06-22",
    methodology: "Fator Médio Urbano",
    description: "Corredor verde ao longo da avenida com cálculo preliminar de sequestro de carbono.",
    createdAt: "2025-05-18",
  },
  {
    id: "cp-008",
    name: "Programa ESG Verde Corporativo",
    type: "ESG Corporativo",
    client: "Grupo Empresarial Verde",
    municipality: "Goiânia",
    uf: "GO",
    areaHa: 4.8,
    treesCount: 340,
    biomassEstimated: 102.0,
    carbonEstimated: 40.8,
    co2eEstimated: 149.6,
    confidenceLevel: 80,
    status: "Em monitoramento",
    lastUpdate: "2026-06-26",
    methodology: "DAP por Grupo Funcional",
    description: "Programa ESG com inventário arbóreo das sedes corporativas e estimativa de carbono.",
    createdAt: "2025-10-12",
  },
];

export const treeCarbonEstimates: TreeCarbonEstimate[] = [
  { id: "tce-001", ctaCode: "CTA-GO-001", species: "Mangifera indica", speciesCommon: "Manga", projectId: "cp-001", municipality: "Goiânia", neighborhood: "Jardim Goiás", dap: 45, height: 12, biomassEstimated: 342.5, carbonEstimated: 171.3, co2eEstimated: 628.1, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-20", greenAreaId: "ga-001", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-002", ctaCode: "CTA-GO-002", species: "Caesalpinia pluviosa", speciesCommon: "Sibipiruna", projectId: "cp-001", municipality: "Goiânia", neighborhood: "Jardim Goiás", dap: 38, height: 14, biomassEstimated: 298.7, carbonEstimated: 149.4, co2eEstimated: 547.5, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-18", greenAreaId: "ga-001", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-003", ctaCode: "CTA-GO-003", species: "Cassia fistula", speciesCommon: "Canafístula", projectId: "cp-001", municipality: "Goiânia", neighborhood: "Setor Bueno", dap: 32, height: 10, biomassEstimated: 156.2, carbonEstimated: 78.1, co2eEstimated: 286.3, methodology: "DAP + Altura por Espécie", confidenceLevel: "média", lastInspection: "2026-05-30", greenAreaId: "ga-002", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-004", ctaCode: "CTA-GO-004", species: "Cedrela fissilis", speciesCommon: "Cedro", projectId: "cp-002", municipality: "Goiânia", neighborhood: "Jardim América", dap: 52, height: 18, biomassEstimated: 480.5, carbonEstimated: 240.3, co2eEstimated: 880.8, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-25", greenAreaId: "ga-003", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-005", ctaCode: "CTA-GO-005", species: "Schinus terebinthifolia", speciesCommon: "Aroeira", projectId: "cp-002", municipality: "Goiânia", neighborhood: "Jardim América", dap: 28, height: 8, biomassEstimated: 98.4, carbonEstimated: 49.2, co2eEstimated: 180.3, methodology: "DAP por Grupo Funcional", confidenceLevel: "média", lastInspection: "2026-06-15", greenAreaId: "ga-003", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-006", ctaCode: "CTA-GO-006", species: "Hymenaea courbaril", speciesCommon: "Jatobá", projectId: "cp-003", municipality: "Aparecida de Goiânia", neighborhood: "Buritis", dap: 65, height: 22, biomassEstimated: 720.0, carbonEstimated: 360.0, co2eEstimated: 1320.0, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-20", greenAreaId: "ga-004", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-007", ctaCode: "CTA-GO-007", species: "Tectona grandis", speciesCommon: "Teca", projectId: "cp-003", municipality: "Aparecida de Goiânia", neighborhood: "Buritis", dap: 42, height: 16, biomassEstimated: 380.2, carbonEstimated: 190.1, co2eEstimated: 696.9, methodology: "DAP + Altura por Espécie", confidenceLevel: "média", lastInspection: "2026-06-12", greenAreaId: "ga-004", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-008", ctaCode: "CTA-GO-008", species: "Tabebuia roseoalba", speciesCommon: "Ipê-branco", projectId: "cp-004", municipality: "Goiânia", neighborhood: "Setor Oeste", dap: 35, height: 11, biomassEstimated: 210.5, carbonEstimated: 105.3, co2eEstimated: 386.0, methodology: "DAP + Altura por Espécie", confidenceLevel: "média", lastInspection: "2026-06-27", greenAreaId: "ga-005", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-009", ctaCode: "CTA-GO-009", species: "Delonix regia", speciesCommon: "Flamboyant", projectId: "cp-004", municipality: "Goiânia", neighborhood: "Setor Oeste", dap: 40, height: 12, biomassEstimated: 280.0, carbonEstimated: 140.0, co2eEstimated: 513.3, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-27", greenAreaId: "ga-005", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-010", ctaCode: "CTA-GO-010", species: "Samanea saman", speciesCommon: "Rainha", projectId: "cp-005", municipality: "Anápolis", neighborhood: "Industrial", dap: 58, height: 20, biomassEstimated: 560.0, carbonEstimated: 280.0, co2eEstimated: 1026.7, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-15", greenAreaId: "ga-006", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-011", ctaCode: "CTA-GO-011", species: "Spondias tuberosa", speciesCommon: "Umbu", projectId: "cp-005", municipality: "Anápolis", neighborhood: "Industrial", dap: 22, height: 6, biomassEstimated: 45.0, carbonEstimated: 22.5, co2eEstimated: 82.5, methodology: "Modelo Simplificado por DAP", confidenceLevel: "baixa", lastInspection: "2026-05-20", greenAreaId: "ga-006", hasDap: true, hasHeight: false, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-012", ctaCode: "CTA-GO-012", species: "Tapirira guianensis", speciesCommon: "Cupiúba", projectId: "cp-006", municipality: "Goiânia", neighborhood: "Parque Buriti", dap: 48, height: 15, biomassEstimated: 420.0, carbonEstimated: 210.0, co2eEstimated: 770.0, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-28", greenAreaId: "ga-007", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-013", ctaCode: "CTA-GO-013", species: "Psidium guajava", speciesCommon: "Goiabeira", projectId: "cp-006", municipality: "Goiânia", neighborhood: "Parque Buriti", dap: 26, height: 5, biomassEstimated: 65.0, carbonEstimated: 32.5, co2eEstimated: 119.2, methodology: "DAP por Grupo Funcional", confidenceLevel: "média", lastInspection: "2026-06-10", greenAreaId: "ga-007", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-014", ctaCode: "CTA-GO-014", species: "Anacardium occidentale", speciesCommon: "Caju", projectId: "cp-007", municipality: "Goiânia", neighborhood: "Jardim Goiás", dap: 44, height: 13, biomassEstimated: 310.0, carbonEstimated: 155.0, co2eEstimated: 568.3, methodology: "Fator Médio Urbano", confidenceLevel: "média", lastInspection: "2026-06-22", greenAreaId: "ga-008", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-015", ctaCode: "CTA-GO-015", species: "Byrsonima crassifolia", speciesCommon: "Murici", projectId: "cp-007", municipality: "Goiânia", neighborhood: "Setor Bueno", dap: 18, height: 4, biomassEstimated: 28.0, carbonEstimated: 14.0, co2eEstimated: 51.3, methodology: "Modelo Simplificado por DAP", confidenceLevel: "baixa", lastInspection: "2026-04-15", greenAreaId: null, hasDap: true, hasHeight: false, hasSpecies: true, hasCoords: false, hasInspection: false },
  { id: "tce-016", ctaCode: "CTA-GO-016", species: "Swietenia macrophylla", speciesCommon: "Mogno", projectId: "cp-008", municipality: "Goiânia", neighborhood: "Jardim Goiás", dap: 55, height: 19, biomassEstimated: 550.0, carbonEstimated: 275.0, co2eEstimated: 1008.3, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-24", greenAreaId: "ga-009", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-017", ctaCode: "CTA-GO-017", species: "Gossweilerodendron balsamiferum", speciesCommon: "Agaré", projectId: "cp-008", municipality: "Goiânia", neighborhood: "Setor Oeste", dap: 30, height: 9, biomassEstimated: 120.0, carbonEstimated: 60.0, co2eEstimated: 220.0, methodology: "DAP por Grupo Funcional", confidenceLevel: "média", lastInspection: "2026-06-18", greenAreaId: "ga-009", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-018", ctaCode: "CTA-GO-018", species: "Licania tomentosa", speciesCommon: "Oiti", projectId: "cp-001", municipality: "Goiânia", neighborhood: "Jardim Goiás", dap: 0, height: 0, biomassEstimated: 0, carbonEstimated: 0, co2eEstimated: 0, methodology: "Modelo Simplificado por DAP", confidenceLevel: "baixa", lastInspection: "2025-12-10", greenAreaId: null, hasDap: false, hasHeight: false, hasSpecies: false, hasCoords: true, hasInspection: false },
  { id: "tce-019", ctaCode: "CTA-GO-019", species: "Peltophorum dubium", speciesCommon: "Canafístula-do-brejo", projectId: "cp-003", municipality: "Aparecida de Goiânia", neighborhood: "Buritis", dap: 36, height: 10, biomassEstimated: 185.0, carbonEstimated: 92.5, co2eEstimated: 339.2, methodology: "DAP + Altura por Espécie", confidenceLevel: "média", lastInspection: "2026-06-05", greenAreaId: "ga-004", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
  { id: "tce-020", ctaCode: "CTA-GO-020", species: "Balfourodendron riedelianum", speciesCommon: "Pau-marfim", projectId: "cp-006", municipality: "Goiânia", neighborhood: "Parque Buriti", dap: 50, height: 17, biomassEstimated: 450.0, carbonEstimated: 225.0, co2eEstimated: 825.0, methodology: "DAP + Altura por Espécie", confidenceLevel: "alta", lastInspection: "2026-06-28", greenAreaId: "ga-007", hasDap: true, hasHeight: true, hasSpecies: true, hasCoords: true, hasInspection: true },
];

export const carbonAreas: CarbonArea[] = [
  { id: "ga-001", name: "Praça Jardim Goiás", type: "Praça", areaHa: 1.2, treesCount: 156, co2eEstimated: 68.5, criticalRiskCount: 2, confidenceLevel: 80, status: "Monitorada", municipality: "Goiânia", projectId: "cp-001" },
  { id: "ga-002", name: "Praça do Sol", type: "Praça", areaHa: 0.8, treesCount: 98, co2eEstimated: 42.3, criticalRiskCount: 1, confidenceLevel: 72, status: "Monitorada", municipality: "Goiânia", projectId: "cp-001" },
  { id: "ga-003", name: "Área Verde Jardim América", type: "Área Verde", areaHa: 5.4, treesCount: 320, co2eEstimated: 145.8, criticalRiskCount: 3, confidenceLevel: 78, status: "Monitorada", municipality: "Goiânia", projectId: "cp-002" },
  { id: "ga-004", name: "Reserva Ecológica Buritis", type: "UC", areaHa: 28.5, treesCount: 1560, co2eEstimated: 763.6, criticalRiskCount: 5, confidenceLevel: 65, status: "Em levantamento", municipality: "Aparecida de Goiânia", projectId: "cp-003" },
  { id: "ga-005", name: "APP Córrego Matinha", type: "APP Urbana", areaHa: 8.4, treesCount: 620, co2eEstimated: 272.7, criticalRiskCount: 8, confidenceLevel: 70, status: "Monitorada", municipality: "Goiânia", projectId: "cp-004" },
  { id: "ga-006", name: "Área Recuperada Industrial", type: "Área de Recuperação", areaHa: 15.6, treesCount: 1100, co2eEstimated: 484.6, criticalRiskCount: 4, confidenceLevel: 60, status: "Planejada", municipality: "Anápolis", projectId: "cp-005" },
  { id: "ga-007", name: "Parque Municipal Buriti Sereno", type: "Parque", areaHa: 32.0, treesCount: 2100, co2eEstimated: 997.3, criticalRiskCount: 6, confidenceLevel: 85, status: "Monitorada", municipality: "Goiânia", projectId: "cp-006" },
  { id: "ga-008", name: "Corredor Verde T-63", type: "Corredor Verde", areaHa: 6.2, treesCount: 450, co2eEstimated: 198.0, criticalRiskCount: 1, confidenceLevel: 75, status: "Em estimativa", municipality: "Goiânia", projectId: "cp-007" },
  { id: "ga-009", name: "Sede Corporativa Verde", type: "Área Verde", areaHa: 2.1, treesCount: 180, co2eEstimated: 82.5, criticalRiskCount: 0, confidenceLevel: 80, status: "Monitorada", municipality: "Goiânia", projectId: "cp-008" },
];

export const carbonCompensations: CarbonCompensation[] = [
  { id: "cc-001", projectId: "cp-002", projectName: "Arborização Loteamento Jardim Verde", areaName: "Área Verde Jardim América", obligationType: "Compensação por supressão", seedlingsPlanned: 500, seedlingsPlanted: 420, survivalRate: 84, inspectionsDone: 6, co2ePreliminary: 145.8, status: "Em monitoramento", lastInspection: "2026-06-20" },
  { id: "cc-002", projectId: "cp-003", projectName: "Compensação Reserva Ecológica Buritis", areaName: "Reserva Ecológica Buritis", obligationType: "Compensação de APP", seedlingsPlanned: 2000, seedlingsPlanted: 1650, survivalRate: 82.5, inspectionsDone: 8, co2ePreliminary: 763.6, status: "Em monitoramento", lastInspection: "2026-06-15" },
  { id: "cc-003", projectId: "cp-004", projectName: "Reflorestamento APP Córrego Matinha", areaName: "APP Córrego Matinha", obligationType: "Reflorestamento obrigatório", seedlingsPlanned: 800, seedlingsPlanted: 620, survivalRate: 77.5, inspectionsDone: 5, co2ePreliminary: 272.7, status: "Em plantio", lastInspection: "2026-06-10" },
  { id: "cc-004", projectId: "cp-005", projectName: "Recuperação Área Degradada Setor Industrial", areaName: "Área Recuperada Industrial", obligationType: "Recuperação de área degradada", seedlingsPlanned: 1200, seedlingsPlanted: 0, survivalRate: 0, inspectionsDone: 1, co2ePreliminary: 484.6, status: "Planejada", lastInspection: "2026-05-01" },
  { id: "cc-005", projectId: "cp-007", projectName: "Corredor Verde Avenida T-63", areaName: "Corredor Verde T-63", obligationType: "Compensação de corredor viário", seedlingsPlanned: 300, seedlingsPlanted: 280, survivalRate: 93.3, inspectionsDone: 4, co2ePreliminary: 198.0, status: "Cumprida", lastInspection: "2026-06-22" },
  { id: "cc-006", projectId: "cp-008", projectName: "Programa ESG Verde Corporativo", areaName: "Sede Corporativa Verde", obligationType: "Compensação corporativa voluntária", seedlingsPlanned: 200, seedlingsPlanted: 180, survivalRate: 90, inspectionsDone: 3, co2ePreliminary: 82.5, status: "Em monitoramento", lastInspection: "2026-06-18" },
];

export const carbonMethodologies: CarbonMethodology[] = [
  { id: "cm-001", name: "Modelo Simplificado por DAP", description: "Estimativa rápida de biomassa arbórea utilizando apenas o DAP (Diâmetro à Altura do Peito) e fatores médios para espécies cadastradas na região.", requiredData: ["DAP", "Espécie cadastrada"], precisionLevel: "Baixa (±40%)", limitations: "Não considera altura real, condição fitossanitária, idade ou fatores locais específicos.", status: "ativa", type: "Estimativa individual" },
  { id: "cm-002", name: "Modelo por DAP + Altura", description: "Modelo aprimorado que incorpora altura total da árvore junto ao DAP para estimar volume de madeira e biomassa com maior precisão.", requiredData: ["DAP", "Altura total", "Espécie cadastrada"], precisionLevel: "Média (±25%)", limitations: "Requer medição de altura precisa. Pode subestimar para árvores com copa incomum.", status: "ativa", type: "Estimativa individual" },
  { id: "cm-003", name: "Modelo por Espécie / Grupo Funcional", description: "Utiliza equações alométricas específicas por espécie ou grupo funcional (pioneira, secundária inicial, secundária tardia, climácica).", requiredData: ["DAP", "Altura", "Espécie identificada com certeza", "Grupo funcional"], precisionLevel: "Alta (±15%)", limitations: "Exige identificação botânica precisa. Nem todas as espécies possuem equações alométricas publicadas.", status: "ativa", type: "Estimativa individual" },
  { id: "cm-004", name: "Fator Médio Urbano", description: "Aplicação de fator médio de biomassa por hectare para árvores urbanas, baseado em inventários regionais e literatura científica.", requiredData: ["Área (ha)", "Densidade de árvores", "Tipo de vegetação"], precisionLevel: "Média (±30%)", limitations: "Não considera variações individuais. Adequado para estimativas de corredores e áreas extensas.", status: "ativa", type: "Estimativa por área" },
  { id: "cm-005", name: "Estimativa por Área Verde", description: "Modelo baseado no tipo de área verde (parque, praça, APP, UC) com fatores médios de biomassa/hectare por categoria.", requiredData: ["Área (ha)", "Tipo de área verde", "Número de árvores"], precisionLevel: "Média (±35%)", limitations: "Varia muito conforme o manejo da área. Requer ajuste por bioma e região.", status: "ativa", type: "Estimativa por área" },
  { id: "cm-006", name: "Estimativa Preliminar para Compensação", description: "Modelo simplificado para compensação ambiental, considerando mudas plantadas, taxa de sobrevivência e crescimento esperado.", requiredData: ["Quantidade de mudas", "Taxa de sobrevivência", "Idade das mudas", "Espécies"], precisionLevel: "Baixa (±50%)", limitations: "Altamente incerto devido à variabilidade da sobrevivência e crescimento. Deve ser revisado anualmente.", status: "ativa", type: "Compensação" },
];

export const carbonReports: CarbonReport[] = [
  { id: "cr-001", title: "Relatório Preliminar de Carbono Urbano", description: "Panorama geral das estimativas de carbono do território urbano vinculado ao CTA.", lastGenerated: "2026-06-15", status: "disponível", format: "PDF", type: "preliminar" },
  { id: "cr-002", title: "Relatório de Biomassa Arbórea", description: "Detalhamento das estimativas de biomassa por espécie, bairro e projeto.", lastGenerated: "2026-06-10", status: "disponível", format: "PDF / Excel", type: "biomassa" },
  { id: "cr-003", title: "Relatório por Projeto de Carbono", description: "Análise individual de cada projeto de carbono com indicadores e qualidade dos dados.", lastGenerated: "2026-06-20", status: "disponível", format: "PDF", type: "projeto" },
  { id: "cr-004", title: "Relatório por Área Verde", description: "Estimativas de carbono agrupadas por tipo de área verde: praças, parques, UCs e APPs.", lastGenerated: "2026-05-28", status: "disponível", format: "PDF / Excel", type: "area" },
  { id: "cr-005", title: "Relatório por Unidade de Conservação", description: "Relatório técnico focado nas UCs vinculadas ao módulo de carbono.", lastGenerated: "2026-06-01", status: "disponível", format: "PDF", type: "uc" },
  { id: "cr-006", title: "Relatório de Compensação Ambiental", description: "Acompanhamento de compensações, replantios, sobrevivência e CO2e preliminar.", lastGenerated: "2026-06-18", status: "disponível", format: "PDF", type: "compensação" },
  { id: "cr-007", title: "Relatório ESG Ambiental", description: "Relatório com indicadores ESG ambientais para empresas e instituições.", lastGenerated: null, status: "pendente", format: "PDF", type: "esg" },
  { id: "cr-008", title: "Relatório Executivo para Prefeitura", description: "Resumo executivo com principais indicadores de carbono para tomada de decisão pública.", lastGenerated: "2026-06-25", status: "disponível", format: "PDF", type: "executivo" },
];

export const carbonMonthlyData: CarbonMonthlyData[] = [
  { month: "Jan", co2e: 3850, carbon: 1050, biomass: 2620 },
  { month: "Fev", co2e: 3890, carbon: 1061, biomass: 2647 },
  { month: "Mar", co2e: 3940, carbon: 1075, biomass: 2681 },
  { month: "Abr", co2e: 3980, carbon: 1086, biomass: 2709 },
  { month: "Mai", co2e: 4050, carbon: 1105, biomass: 2757 },
  { month: "Jun", co2e: 4100, carbon: 1118, biomass: 2791 },
  { month: "Jul", co2e: 4150, carbon: 1132, biomass: 2825 },
  { month: "Ago", co2e: 4190, carbon: 1143, biomass: 2852 },
  { month: "Set", co2e: 4220, carbon: 1151, biomass: 2872 },
  { month: "Out", co2e: 4240, carbon: 1157, biomass: 2887 },
  { month: "Nov", co2e: 4260, carbon: 1162, biomass: 2899 },
  { month: "Dez", co2e: 4280, carbon: 1167, biomass: 2914 },
];

export const carbonByProjectData: CarbonByProjectData[] = [
  { project: "Inventário Goiânia Norte", co2e: 1265.0, carbon: 345.0, biomass: 862.4 },
  { project: "Loteamento Jardim Verde", co2e: 359.5, carbon: 98.1, biomass: 245.3 },
  { project: "Reserva Buritis", co2e: 763.6, carbon: 208.3, biomass: 520.7 },
  { project: "APP Córrego Matinha", co2e: 272.7, carbon: 74.4, biomass: 186.0 },
  { project: "Área Degradada Industrial", co2e: 484.6, carbon: 132.2, biomass: 330.5 },
  { project: "Parque Buriti Sereno", co2e: 997.3, carbon: 272.0, biomass: 680.0 },
  { project: "Corredor Verde T-63", co2e: 198.0, carbon: 54.0, biomass: 135.0 },
  { project: "ESG Verde Corporativo", co2e: 149.6, carbon: 40.8, biomass: 102.0 },
];

export const carbonByAreaTypeData: CarbonByAreaTypeData[] = [
  { type: "Árvores Urbanas", co2e: 1850.0, label: "Árvores Urbanas" },
  { type: "Áreas Verdes", co2e: 920.0, label: "Áreas Verdes" },
  { type: "UCs", co2e: 860.0, label: "Unidades de Conservação" },
  { type: "Compensações", co2e: 410.0, label: "Compensações" },
  { type: "Recuperação", co2e: 240.0, label: "Recuperação" },
];

export const carbonQualityMetrics: CarbonQualityMetric[] = [
  { label: "Árvores com DAP preenchido", value: 88, target: 95, unit: "%", status: "regular" },
  { label: "Árvores com altura preenchida", value: 72, target: 90, unit: "%", status: "ruim" },
  { label: "Árvores com espécie identificada", value: 94, target: 95, unit: "%", status: "bom" },
  { label: "Árvores com coordenada válida", value: 96, target: 98, unit: "%", status: "bom" },
  { label: "Árvores com vistoria validada", value: 85, target: 90, unit: "%", status: "regular" },
  { label: "Estimativas com confiança alta", value: 68, target: 75, unit: "%", status: "regular" },
];

export const carbonAlerts: CarbonAlert[] = [
  { id: "ca-001", type: "DAP ausente", message: "Árvores cadastradas sem DAP preenchido comprometem a estimativa de biomassa.", severity: "atenção", count: 1313, action: "Completar medições de campo" },
  { id: "ca-002", type: "Espécie não identificada", message: "Árvores sem espécie identificada utilizam fator médio genérico, reduzindo a precisão.", severity: "cautela", count: 656, action: "Identificar botanicamente" },
  { id: "ca-003", type: "Área sem polígono", message: "Áreas verdes e UCs sem polígono definido não participam do cálculo por área.", severity: "atenção", count: 3, action: "Cadastrar polígono GIS" },
  { id: "ca-004", type: "Projeto sem metodologia", message: "Projetos de carbono sem metodologia definida não geram estimativas.", severity: "crítico", count: 1, action: "Definir metodologia aplicada" },
  { id: "ca-005", type: "Estimativa de baixa confiança", message: "Árvores com estimativa de baixa confiança devem ser priorizadas para vistoria.", severity: "cautela", count: 2186, action: "Realizar vistoria técnica" },
  { id: "ca-006", type: "Compensação sem monitoramento", message: "Compensações ambientais sem vistoria recente podem estar subestimando perdas.", severity: "atenção", count: 2, action: "Agendar vistoria de campo" },
];

export const carbonNeighborhoodRank: CarbonNeighborhoodRank[] = [
  { name: "Parque Buriti", co2e: 997.3, treesCount: 2100, avgConfidence: 85 },
  { name: "Jardim Goiás", co2e: 628.1, treesCount: 1560, avgConfidence: 78 },
  { name: "Buritis", co2e: 763.6, treesCount: 1560, avgConfidence: 65 },
  { name: "Setor Oeste", co2e: 386.0, treesCount: 720, avgConfidence: 72 },
  { name: "Jardim América", co2e: 359.5, treesCount: 890, avgConfidence: 82 },
  { name: "Área Industrial", co2e: 484.6, treesCount: 1100, avgConfidence: 60 },
  { name: "Setor Bueno", co2e: 286.3, treesCount: 450, avgConfidence: 75 },
];
export const projectTypes = [
  "Carbono Urbano",
  "Arborização Urbana",
  "Compensação Ambiental",
  "Replantio",
  "Recuperação de Área Degradada",
  "UC / Área Protegida",
  "Corredor Verde",
  "ESG Corporativo",
];

export const projectStatuses = [
  "Planejado",
  "Em levantamento",
  "Em estimativa",
  "Em monitoramento",
  "Em revisão",
  "Concluído",
  "Arquivado",
];

export const compensationStatuses = [
  "Planejada",
  "Em plantio",
  "Em monitoramento",
  "Parcialmente cumprida",
  "Cumprida",
  "Pendente",
  "Vencida",
];